import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Building2, Network, ChevronRight, Calendar, MapPin, Edit, Trash2, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import ProjectMilestoneForm from './ProjectMilestoneForm';
import SegmentMilestoneForm from './SegmentMilestoneForm';
import TowerMilestoneForm from './TowerMilestoneForm';
import { getAllProjects, getProjectDetails, deleteProject } from '../../services/projectService';

/**
 * ProjectMilestoneList - INTEGRATED VERSION
 * 
 * Features:
 * - List all projects with real API data
 * - View project details (segments/tower sites)
 * - Create/Edit/Delete projects
 * - Setup milestones for segments/sites
 * - Search and filter functionality
 * 
 * API Integration:
 * - GET /api/projects - List all projects (lightweight)
 * - GET /api/projects/{id}/details - Get project with segments/sites
 * - POST /api/projects - Create project
 * - PUT /api/projects/{id} - Update project
 * - DELETE /api/projects/{id} - Delete project
 */
const ProjectMilestoneList = () => {
  // State
  const [projects, setProjects] = useState([]);
  const [segments, setSegments] = useState([]);
  const [towerSites, setTowerSites] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedTowerSite, setSelectedTowerSite] = useState(null);
  const [view, setView] = useState('list'); // 'list', 'project-form', 'project-detail', 'segment-milestone', 'tower-milestone'
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('segments'); // 'segments' or 'towers'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  /**
   * Fetch all projects from API
   */
  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getAllProjects();
      setProjects(data);
      console.log('📥 Loaded projects:', data.length);
    } catch (err) {
      console.error('❌ Error loading projects:', err);
      setError('Failed to load projects');
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch project details with segments/sites
   */
  const fetchProjectDetails = async (projectId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const projectDetails = await getProjectDetails(projectId);
      
      // Extract segments or tower sites based on project type
      if (projectDetails.projectType === 'FO') {
        setSegments(projectDetails.foSegments || []);
        setTowerSites([]);
        setActiveTab('segments');
      } else if (projectDetails.projectType === 'TOWER') {
        setTowerSites(projectDetails.towerSites || []);
        setSegments([]);
        setActiveTab('towers');
      }
      
      console.log('📥 Loaded project details:', {
        projectId,
        segments: projectDetails.foSegments?.length || 0,
        towers: projectDetails.towerSites?.length || 0
      });
      
    } catch (err) {
      console.error('❌ Error loading project details:', err);
      setError('Failed to load project details');
      toast.error('Failed to load project details');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle project card click - load details
   */
  const handleProjectClick = (project) => {
    setSelectedProject(project);
    fetchProjectDetails(project.id);
    setView('project-detail');
  };

  /**
   * Navigate back to project list
   */
  const handleBackToList = () => {
    setSelectedProject(null);
    setSegments([]);
    setTowerSites([]);
    setActiveTab('segments');
    setView('list');
  };

  /**
   * Show add project form
   */
  const handleAddProject = () => {
    setFormMode('add');
    setSelectedProject(null);
    setView('project-form');
  };

  /**
   * Show edit project form
   */
  const handleEditProject = (project) => {
    setFormMode('edit');
    setSelectedProject(project);
    setView('project-form');
  };

  /**
   * Delete project
   */
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteProject(projectId);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success('Project deleted successfully');
      console.log('✅ Project deleted:', projectId);
    } catch (err) {
      console.error('❌ Error deleting project:', err);
      toast.error('Failed to delete project');
    }
  };

  /**
   * Setup milestone for segment/site
   */
  const handleSetMilestone = (item, type) => {
    if (type === 'segment') {
      setSelectedSegment(item);
      setView('segment-milestone');
    } else {
      setSelectedTowerSite(item);
      setView('tower-milestone');
    }
  };

  /**
   * Back from form
   */
  const handleBackFromForm = () => {
    setView('list');
    setSelectedProject(null);
    setFormMode('add');
  };

  /**
   * Back from milestone form
   */
  const handleBackFromMilestone = () => {
    setView('project-detail');
    setSelectedSegment(null);
    setSelectedTowerSite(null);
  };

  /**
   * Handle milestone saved - refresh data
   */
  const handleSaveMilestone = async (id) => {
    // Refresh project details to get updated milestone status
    if (selectedProject) {
      await fetchProjectDetails(selectedProject.id);
    }
    handleBackFromMilestone();
    toast.success('Milestone saved successfully');
  };

  /**
   * Handle project saved - refresh list
   */
  const handleSaveProject = async (projectData) => {
    // The form will handle API call and return success
    // Just refresh the list
    await fetchProjects();
    handleBackFromForm();
  };

  /**
   * Filter projects by search and status
   */
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.projectCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // ========================================
  // RENDER: Project Form
  // ========================================
  if (view === 'project-form') {
    return (
      <ProjectMilestoneForm
        projectData={selectedProject}
        mode={formMode}
        onBack={handleBackFromForm}
        onSave={handleSaveProject}
      />
    );
  }

  // ========================================
  // RENDER: Segment Milestone Form
  // ========================================
  if (view === 'segment-milestone' && selectedSegment) {
    return (
      <SegmentMilestoneForm
        segment={selectedSegment}
        project={selectedProject}
        onBack={handleBackFromMilestone}
        onSave={handleSaveMilestone}
      />
    );
  }

  // ========================================
  // RENDER: Tower Milestone Form
  // ========================================
  if (view === 'tower-milestone' && selectedTowerSite) {
    return (
      <TowerMilestoneForm
        site={selectedTowerSite}
        project={selectedProject}
        onBack={handleBackFromMilestone}
        onSave={handleSaveMilestone}
      />
    );
  }

  // ========================================
  // RENDER: Main View (List or Detail)
  // ========================================
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Project Milestone Planning</h1>
            <p className="text-gray-600 mt-1">
              {view === 'project-detail' && selectedProject 
                ? `Managing milestones for ${selectedProject.projectName}`
                : 'Manage project milestones and track progress'}
            </p>
          </div>
          {view === 'project-detail' && selectedProject ? (
            <button
              onClick={handleBackToList}
              className="bg-gray-500 hover:bg-gray-600 text-white font-medium px-6 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2"
            >
              Back to Projects
            </button>
          ) : (
            <button
              onClick={handleAddProject}
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create New Project
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{error}</p>
            <button
              onClick={fetchProjects}
              className="text-sm text-red-600 hover:text-red-700 font-medium mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {view === 'list' ? (
        // ========================================
        // PROJECT LIST VIEW
        // ========================================
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          {/* Search and Filter Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search projects by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="PLANNING">Planning</option>
                <option value="ON_PROGRESS">On Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="p-12 text-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading projects...</p>
            </div>
          )}

          {/* Project Cards */}
          {!isLoading && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onProjectClick={handleProjectClick}
                    onEditProject={handleEditProject}
                    onDeleteProject={handleDeleteProject}
                  />
                ))}
              </div>

              {filteredProjects.length === 0 && (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No projects found</p>
                  <button
                    onClick={handleAddProject}
                    className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Create your first project
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        // ========================================
        // PROJECT DETAIL VIEW
        // ========================================
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          {/* Project Info Header */}
          <ProjectDetailHeader project={selectedProject} />

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              {selectedProject?.projectType === 'FO' && (
                <button
                  onClick={() => setActiveTab('segments')}
                  className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                    activeTab === 'segments'
                      ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Network className="w-5 h-5" />
                    FO Segments ({segments.length})
                  </div>
                </button>
              )}
              {selectedProject?.projectType === 'TOWER' && (
                <button
                  onClick={() => setActiveTab('towers')}
                  className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                    activeTab === 'towers'
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Tower Sites ({towerSites.length})
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading details...</p>
              </div>
            ) : activeTab === 'segments' && selectedProject?.projectType === 'FO' ? (
              <SegmentsTable 
                segments={segments}
                onSetMilestone={handleSetMilestone}
              />
            ) : activeTab === 'towers' && selectedProject?.projectType === 'TOWER' ? (
              <TowerSitesTable
                towerSites={towerSites}
                onSetMilestone={handleSetMilestone}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// SUB-COMPONENTS
// ========================================

/**
 * Project Card Component
 */
const ProjectCard = ({ project, onProjectClick, onEditProject, onDeleteProject }) => {
  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'ON_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'PLANNING':
        return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'ON_HOLD':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status display
  const formatStatus = (status) => {
    return status.replace('_', ' ');
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('id-ID');
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-gray-200 rounded-lg hover:border-red-500 hover:shadow-xl transition-all">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            project.projectType === 'TOWER' 
              ? 'bg-gradient-to-br from-blue-600 to-blue-800' 
              : 'bg-gradient-to-br from-green-600 to-green-800'
          }`}>
            {project.projectType === 'TOWER' ? (
              <Building2 className="w-6 h-6 text-white" />
            ) : (
              <Network className="w-6 h-6 text-white" />
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
            {formatStatus(project.status)}
          </span>
        </div>
        
        <h3 className="text-lg font-bold text-gray-800 mb-1">
          {project.projectName}
        </h3>
        <p className="text-sm text-gray-600 mb-1">{project.projectCode}</p>
        <p className="text-xs text-gray-500 mb-4">
          {project.spks && project.spks.length > 0 ? project.spks[0].spkNumber : 'No SPK'}
        </p>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <Building2 className="w-4 h-4 mr-2 text-blue-600" />
            {project.customerName}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="w-4 h-4 mr-2 text-blue-600" />
            {formatDate(project.startDate)} - {formatDate(project.targetEndDate)}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            {project.projectType === 'TOWER' ? (
              <>
                <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                {project.towerSiteCount || 0} Tower Sites
              </>
            ) : (
              <>
                <Network className="w-4 h-4 mr-2 text-green-600" />
                {project.foSegmentCount || 0} FO Segments
              </>
            )}
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 flex gap-2">
          <button 
            onClick={() => onProjectClick(project)}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            Manage Milestones
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditProject(project);
            }}
            className="p-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteProject(project.id);
            }}
            className="p-2 border-2 border-red-600 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Project Detail Header Component
 */
const ProjectDetailHeader = ({ project }) => {
  if (!project) return null;

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('id-ID');
  };

  return (
    <div className="p-6 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-t-lg">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-medium text-blue-200 mb-1">Project Code</label>
          <p className="text-sm font-semibold">{project.projectCode}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-blue-200 mb-1">Project Type</label>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            project.projectType === 'TOWER' 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {project.projectType}
          </span>
        </div>
        <div>
          <label className="block text-xs font-medium text-blue-200 mb-1">Customer</label>
          <p className="text-sm font-semibold">{project.customerName}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-blue-200 mb-1">Project Period</label>
          <p className="text-sm font-semibold">
            {formatDate(project.startDate)} - {formatDate(project.targetEndDate)}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * FO Segments Table Component
 */
const SegmentsTable = ({ segments, onSetMilestone }) => {
  if (!segments || segments.length === 0) {
    return (
      <div className="text-center py-12">
        <Network className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No segments found for this project</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-green-600 text-white">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Span ID</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Segment Name</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Main Area</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Length (km)</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Location</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {segments.map((segment) => (
            <tr key={segment.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-semibold text-gray-900">{segment.spanId}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900">{segment.siteNameAB}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-600">{segment.mainArea}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{segment.commercialDistance} km</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {segment.kota}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {segment.hasMilestones ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    Milestone Set
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                    No Milestone
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => onSetMilestone(segment, 'segment')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-md hover:shadow-lg ${
                    segment.hasMilestones
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {segment.hasMilestones ? 'Edit Milestone' : 'Set Milestone'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Tower Sites Table Component
 */
const TowerSitesTable = ({ towerSites, onSetMilestone }) => {
  if (!towerSites || towerSites.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No tower sites found for this project</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-blue-600 text-white">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Site ID</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Site Name</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Location</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Regional</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {towerSites.map((site) => (
            <tr key={site.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-semibold text-gray-900">{site.siteId}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900">{site.siteName}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {site.location || '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{site.regional}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                  {site.typeSiteContract}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {site.hasMilestones ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    Milestone Set
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                    No Milestone
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => onSetMilestone(site, 'tower')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-md hover:shadow-lg ${
                    site.hasMilestones
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {site.hasMilestones ? 'Edit Milestone' : 'Set Milestone'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProjectMilestoneList;